// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import { X } from 'lucide-react'
import { setApiErrorSink } from '../../api/client'

/**
 * The app's one toast queue (F-02 global API-error sink, de-duplication) — D-UI-3 c4 rebuilt on MUI:
 * - the colour comes from an explicit `type`, never guessed from the message (CareRx audit C11, E9);
 * - errors stay until dismissed; success / info hide after 5 s;
 * - optional action (e.g. Undo);
 * - at most 5 on screen: an overflow drops the oldest NON-error first, so a later success never pushes
 *   an error out of sight;
 * - cleared when the signed-in account or pharmacy changes (`ToastSessionGuard`), so a message about one
 *   session never shows to the next. Messages are never logged or stored.
 */
export type ToastType = 'error' | 'success' | 'info'
export interface ToastAction { label: string; onClick: () => void }
interface ToastItem { id: number; message: string; type: ToastType; action?: ToastAction }
interface ToastCtx {
  show: (message: string, type?: ToastType, opts?: { action?: ToastAction }) => void
  clear: () => void
}

const Ctx = createContext<ToastCtx>({ show: () => {}, clear: () => {} })
export const useToast = () => useContext(Ctx)

export const AUTO_HIDE_MS = 5000
export const MAX_VISIBLE = 5

/** Pure queue rule (tested): no repeat of a message already showing; at most MAX_VISIBLE — the oldest
 *  non-error goes first, and only when every toast is an error does the oldest error go. */
export function enqueue(items: ToastItem[], next: ToastItem): ToastItem[] {
  if (items.some(t => t.message === next.message && t.type === next.type)) return items
  const out = [...items, next]
  while (out.length > MAX_VISIBLE) {
    const i = out.findIndex(t => t.type !== 'error')
    out.splice(i < 0 ? 0 : i, 1)
  }
  return out
}

let nextId = 1

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: number) => {
    const tm = timers.current.get(id)
    if (tm) { clearTimeout(tm); timers.current.delete(id) }
    setToasts(t => t.filter(x => x.id !== id))
  }, [])

  const show = useCallback((message: string, type: ToastType = 'info', opts?: { action?: ToastAction }) => {
    const id = nextId++
    setToasts(t => enqueue(t, { id, message, type, action: opts?.action }))
    if (type !== 'error') timers.current.set(id, setTimeout(() => dismiss(id), AUTO_HIDE_MS))
  }, [dismiss])

  const clear = useCallback(() => {
    timers.current.forEach(clearTimeout)
    timers.current.clear()
    setToasts([])
  }, [])

  // F-02: surface server/network API failures globally — even where a call site swallows them.
  useEffect(() => {
    setApiErrorSink(msg => show(msg, 'error'))
    return () => setApiErrorSink(null)
  }, [show])
  useEffect(() => {
    const live = timers.current
    return () => { live.forEach(clearTimeout) }
  }, [])

  return (
    <Ctx.Provider value={{ show, clear }}>
      {children}
      <Box aria-live="polite" sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1500, display: 'flex',
        flexDirection: 'column', gap: 1, maxWidth: 'min(380px, calc(100vw - 32px))' }}>
        {toasts.map(t => (
          <Alert key={t.id} severity={t.type} variant="outlined" role={t.type === 'error' ? 'alert' : 'status'}
            onClose={() => dismiss(t.id)}
            // MUI drops its close button when `action` is set; an error never auto-hides, so it keeps one.
            action={t.action ? (<>
              <Button size="small" color="inherit" onClick={() => { t.action?.onClick(); dismiss(t.id) }}>{t.action.label}</Button>
              <IconButton size="small" color="inherit" aria-label="Close" onClick={() => dismiss(t.id)}><X size={14} /></IconButton>
            </>) : undefined}
            sx={{ bgcolor: 'background.paper', boxShadow: 3, fontSize: 13, alignItems: 'center' }}>
            {t.message}
          </Alert>
        ))}
      </Box>
    </Ctx.Provider>
  )
}

/** Clears the queue whenever the signed-in identity (account or pharmacy) changes, sign-out included. */
// The first change after load (signed-out to the real identity) also clears: a toast raised before
// /auth/me answers is about no one yet.
export function ToastSessionGuard({ identity }: { identity: string }) {
  const { clear } = useToast()
  const first = useRef(true)
  useEffect(() => {
    if (first.current) { first.current = false; return }
    clear()
  }, [identity, clear])
  return null
}
